import { useCallback, useRef, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FontSizes, FontWeights, Loader, PrimaryButton, Text } from '@calm-web/design-system';

import { isSubscriptionResponse } from '@/components/purchase/PaymentElementsForm/types';
import Branding from '@/components/SimplifiedSignup/Components/Branding';
import {
	CAMPAIGN_FUNNEL_EVENTS,
	campaignEventProps,
	campaignPartnershipEventProps,
} from '@/components/SimplifiedSignup/configs/Campaigns/analytics';
import { openPostRedemptionCta } from '@/components/SimplifiedSignup/configs/Campaigns/postRedemptionCta';
import { useSimplifiedSignupContext } from '@/components/SimplifiedSignup/Context';
import { useAnalytics } from '@/hooks/analytics';
import { useCampaignConfigState, useDeviceState, useUserState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import genericMessages from '@/messages/messages';
import { setUser } from '@/store/user/actions';
import { postCampaignRedemption } from '@/utils/endpoints';

import messages from './messages';
import { Copy, Wrapper } from './styles';
import type { CampaignRedemptionScreenConfig } from './types';
import { classifyRedemptionFailure, UnusableGrantError, type RedemptionFailure } from './utils';

type Status = { state: 'pending' } | { state: 'failed'; failure: RedemptionFailure };

/**
 * The card-less (brand) campaign's purchase step. There is no payment to take, so this screen
 * posts the validated code to the same purchase endpoint the paid flow uses and, once the grant
 * exists, routes to the handoff. Until then the handoff must not be shown: it is the success
 * screen, and before this call there is nothing to be successful about.
 *
 * The granted subscription is written to the store before routing. Context's routing effect sends
 * a logged-in user without a valid subscription to the payment screen from any screen whose
 * nextScreen is not a function, and a brand campaign must never show that screen.
 */
const CampaignRedemption = (): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const dispatch = useDispatch();
	const user = useUserState();
	const device = useDeviceState();
	const campaign = useCampaignConfigState();
	const { logEvent } = useAnalytics();
	const { activeScreenConfig, routeToScreen, logPageViewEvent, coBranded, logo } =
		useSimplifiedSignupContext();
	const {
		showBranding = false,
		backgroundKey,
		openAppCta,
	} = activeScreenConfig as CampaignRedemptionScreenConfig;
	const [status, setStatus] = useState<Status>({ state: 'pending' });
	const [isOpeningApp, setIsOpeningApp] = useState(false);
	// The ref refuses a second press that lands before React has re-rendered with the pending state.
	const openAppInFlight = useRef(false);

	const redeem = useCallback(async () => {
		if (!campaign) return;
		setStatus({ state: 'pending' });
		const eventProps = { ...campaignEventProps(campaign), ...campaignPartnershipEventProps(campaign) };
		try {
			const { data } = await postCampaignRedemption({
				campaignSlug: campaign.slug,
				campaignId: campaign.id,
				// A brand campaign always takes a code (the api refuses to launch one that does not), and
				// Account only routes here once CampaignCodeEntry has validated it. An empty string here is
				// a bug upstream; the backend answers it with a validation error, shown below.
				code: campaign.offer_code ?? '',
			});
			// A 2xx is not proof of a grant. The paid form guards its response the same way before
			// treating the purchase as done; an empty body or an invalid subscription must not reach
			// the store, the conversion event or the handoff.
			if (!isSubscriptionResponse(data) || !data.valid) {
				throw new UnusableGrantError();
			}
			const subscription = data;
			dispatch(setUser({ ...user, subscription }));
			logEvent({
				eventName: CAMPAIGN_FUNNEL_EVENTS.conversion,
				eventProps: { ...eventProps, conversion_type: 'redemption' },
			});
			routeToScreen('handoff');
		} catch (error) {
			const failure = classifyRedemptionFailure(error);
			logEvent({
				eventName: CAMPAIGN_FUNNEL_EVENTS.redemptionFailure,
				eventProps: {
					...eventProps,
					reason: failure.code ?? 'request_failed',
					status: failure.status,
					outcome: failure.kind,
				},
			});
			setStatus({ state: 'failed', failure });
		}
	}, [campaign, dispatch, logEvent, routeToScreen, user]);

	useComponentDidMount(() => {
		logPageViewEvent('redeem');
		// redeem() settles every rejection into state itself; this catch only satisfies the
		// no-floating-promises lint rule.
		redeem().catch(() => {});
	});

	// One press at a time, same as the handoff's primary CTA: a double tap on a phone would queue a
	// second store fallback. The Promise constructor turns a synchronous throw into a rejection, so
	// the guard is released either way.
	const openApp = (): void => {
		if (openAppInFlight.current) return;
		openAppInFlight.current = true;
		setIsOpeningApp(true);
		const release = (): void => {
			openAppInFlight.current = false;
			setIsOpeningApp(false);
		};
		new Promise<void>(resolve => {
			resolve(openPostRedemptionCta(openAppCta, device));
		}).then(release, release);
	};

	if (!campaign) return null;

	const branding = showBranding && (
		<Branding backgroundColor={backgroundKey} coBranded={coBranded} logo={logo} />
	);

	if (status.state === 'pending') {
		return (
			<Wrapper>
				{branding}
				<Loader color="blue4" />
				<Copy role="status" aria-live="polite">
					<Text noMargin el="h1" size={FontSizes['2xl']} weight={FontWeights.Medium}>
						{formatMessage(messages.pendingHeading)}
					</Text>
					<Text noMargin el="p" size={FontSizes.base} weight={FontWeights.Regular}>
						{formatMessage(messages.pendingBody)}
					</Text>
				</Copy>
			</Wrapper>
		);
	}

	const { failure } = status;
	return (
		<Wrapper>
			{branding}
			<Copy role="alert">
				<Text
					noMargin
					el="h1"
					size={FontSizes['2xl']}
					weight={FontWeights.Medium}
					data-testid="redemption-heading"
				>
					{formatMessage(failure.heading)}
				</Text>
				<Text
					noMargin
					el="p"
					size={FontSizes.base}
					weight={FontWeights.Regular}
					data-testid="redemption-body"
				>
					{formatMessage(failure.body)}
				</Text>
				{campaign.contact_email && (
					<Text noMargin el="p" size={FontSizes.sm} weight={FontWeights.Light}>
						{formatMessage(messages.contactPrompt, { contactEmail: campaign.contact_email })}
					</Text>
				)}
			</Copy>
			{failure.kind === 'retryable' && (
				<PrimaryButton onPress={() => redeem().catch(() => {})}>
					{formatMessage(messages.tryAgainCta)}
				</PrimaryButton>
			)}
			{failure.kind === 'already_has_access' && (
				<PrimaryButton isLoading={isOpeningApp} onPress={openApp}>
					{formatMessage(genericMessages.openCalmApp)}
				</PrimaryButton>
			)}
		</Wrapper>
	);
};

export default CampaignRedemption;
